import React, { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect, useRouter } from 'common/hooks';
import { useDebounced } from 'common/hooks/use-debounced';
import { Suggestions, CategorySuggestion } from 'types/suggestions';
import { PinnedSuggestion, Region } from 'types/model';
import { DEFAULT_SEARCH_PATH } from 'common/constants/paths';
import { getDefaultListNavigablePinnedItems } from '../../components/utils/get-default-list-navigable-pinned-items';
import {
  shouldSplitNewSuggestionsAroundSponsored,
  splitNewSuggestionsForSponsored,
} from '../../components/utils/split-new-suggestions-for-sponsored';
import { getDomain, patchUrlByLocale } from 'utils';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { ProductRedirectionUrl } from 'types/product-url';

import { ProductRedirectionEvents } from 'actions';
import { getIsAkinatorEnabled } from 'selectors/cookie';
import { useSearchbarKeyboardNavigation } from './use-searchbar-keyboard-navigation';
import { shouldShowAkinatorCompletions } from '../../components/utils/should-show-akinator-completions';
import { isAkinatorCompletion } from '../../components/utils/is-akinator-completion';
import { getUpdatedQueryByCompletionType } from '../../components/utils/get-update-query-by-completion-type';
import { sendAkinatorCompletionAnalyticsEvent } from '../../components/akinator/hooks/use-akinator-analytics-event';
import { useSiteDomain } from '../use-site-domain';
import { handleSubmit } from './handle-submit';
import { normalizeInput, getSanitizedValue, sanitizeString } from './utils';
import { TransitionState } from '../../types';
import { useSearchHistoryContext } from '../use-search-history-context';
import { prepareSuggestions } from './prepare-suggestions';
import { useSponsoredProducts } from 'components/header/hooks';
import { getSearchParsed } from 'common/hooks/use-openlink-url/utils';
import { SponsoredProduct } from 'types/sponsored-products';
import { trackingHelper } from 'utils/tracking-helper';
import { handleLmCode } from './handle-lmcode';

type UseSearchbarLogicParams = {
  getSuggestions: (value: string) => void;
  resetServices: () => void;
  collapse: () => void;
  transitionState: TransitionState;
  suggestions: Suggestions | null;
  suggestionsLoading?: boolean;
  error?: boolean;
  productRedirectionUrl?: ProductRedirectionUrl['redirectionUrl'];
  productRedirectionUrlLoading?: boolean;
  currentRegionId?: Region['regionId'];
  resetSponsoredProducts: () => void;
  resetSponsoredBrands: () => void;
  pinnedSuggestions: PinnedSuggestion[];
};

export const useSearchbarLogic = (params: UseSearchbarLogicParams) => {
  const {
    getSuggestions,
    resetServices,
    transitionState,
    collapse,
    productRedirectionUrl,
    productRedirectionUrlLoading,
    currentRegionId,
    suggestions,
    resetSponsoredProducts,
    resetSponsoredBrands,
    suggestionsLoading,
    pinnedSuggestions,
  } = params;

  const router = useRouter();
  const path = router.getPath();
  const { dispatch, host, cookies, model } = useStoreon<State>('host', 'cookies', 'model');
  const domain = getDomain(host);
  const { isKzDomain } = useSiteDomain(domain);
  const isAkinatorEnabled = getIsAkinatorEnabled(cookies);
  const searchHref = model?.header?.linkToSearch || '';

  const search = getSearchParsed();
  const [isSrp, setIsSrp] = useState(
    path.startsWith(patchUrlByLocale({ url: searchHref || DEFAULT_SEARCH_PATH })),
  );

  const [initialValue, setInitialValue] = useState((search.q as string) ?? '');
  const [value, setValue] = useState(initialValue);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { addHistoryFromSubmit, addHistoryItem } = useSearchHistoryContext();
  const sponsoredProducts = useSponsoredProducts();
  const sponsoredSuggestions: CategorySuggestion[] = sponsoredProducts.map(
    (sponsoredItem: SponsoredProduct) => ({
      categoryUrl: sponsoredItem.productPageName,
      categoryName: '',
      categoryLabel: '',
    }),
  );
  const responseId = suggestions?.responseId || '';
  const { slicedSuggestions, flatSuggestions, akinatorCompletions } = useMemo(() => {
    return prepareSuggestions(suggestions, value, searchHref, responseId);
  }, [suggestions, value, searchHref, responseId]);

  const showAkinatorCompletions = shouldShowAkinatorCompletions({
    searchQuery: value,
    completions: akinatorCompletions,
    isKzDomain,
    isAkinatorEnabled,
  });

  const defaultListNavigablePinnedItems = useMemo(() => {
    if (value || isKzDomain) return [];

    return getDefaultListNavigablePinnedItems(pinnedSuggestions);
  }, [value, isKzDomain, pinnedSuggestions]);

  const suggestionItems = useMemo(() => {
    if (!flatSuggestions || !slicedSuggestions) return null;

    const [newSuggestions, categories] = slicedSuggestions;
    const showOnlyCategories = !value && categories.length > 0;
    const shouldSplit = shouldSplitNewSuggestionsAroundSponsored(
      sponsoredSuggestions.length > 0,
      showOnlyCategories,
    );
    const navigableItems = [...defaultListNavigablePinnedItems];

    if (!shouldSplit) {
      return [...navigableItems, ...flatSuggestions, ...sponsoredSuggestions];
    }

    const { leadingNewSuggestions, trailingNewSuggestions } = splitNewSuggestionsForSponsored(
      newSuggestions,
      sponsoredSuggestions.length,
      true,
    );

    return [...navigableItems, ...leadingNewSuggestions, ...sponsoredSuggestions, ...trailingNewSuggestions];
  }, [defaultListNavigablePinnedItems, flatSuggestions, slicedSuggestions, sponsoredSuggestions, value]);

  const { index, reset, selectedNavigableItem, onKeyDown, onMouseEnter, onMouseLeave } =
    useSearchbarKeyboardNavigation({
      akinatorCompletions,
      showAkinatorCompletions,
      suggestionItems,
    });

  const selectedAkinatorCompletion = isAkinatorCompletion(selectedNavigableItem)
    ? selectedNavigableItem
    : null;
  const selectedSuggestion = isAkinatorCompletion(selectedNavigableItem) ? null : selectedNavigableItem;

  const handleGetSuggestions = useCallback(
    (searchString: string) => {
      getSuggestions(searchString);
    },
    [getSuggestions],
  );

  const dGetSuggestions = useDebounced(handleGetSuggestions, { interval: 200 });

  const prettifiedValue = useMemo(() => sanitizeString(value), [value]);

  const isLmcode = useMemo(
    () => /^\d+$/.test(prettifiedValue) && prettifiedValue.length === 8,
    [prettifiedValue],
  );

  const sanitizedValue = getSanitizedValue(selectedSuggestion) ?? prettifiedValue;
  const normalizedSanitizedValue = useMemo(() => normalizeInput(sanitizedValue), [sanitizedValue]);

  const shouldHandleLmcodeSubmit = isLmcode && !productRedirectionUrlLoading && isSubmitted;
  const suggestionsMismatch =
    (suggestions && suggestions.query !== normalizedSanitizedValue) || suggestionsLoading;
  const shouldHandleSuggestionsSubmit =
    isSubmitted && !suggestionsLoading && suggestions && suggestions.query === normalizedSanitizedValue;

  useUpdateEffect(() => {
    setInitialValue((search.q as string) ?? '');
  }, [search]);

  useUpdateEffect(() => {
    setIsSrp(path.startsWith(patchUrlByLocale({ url: searchHref || DEFAULT_SEARCH_PATH })));
  }, [path]);

  useUpdateEffect(() => {
    if (shouldHandleLmcodeSubmit) {
      handleLmCode({
        productRedirectionUrl,
        dispatch,
        setIsSubmitted,
        collapse,
        addHistoryItem,
        value,
        sanitizedValue,
        searchHref,
      });
    }
  }, [shouldHandleLmcodeSubmit, productRedirectionUrl]);

  useUpdateEffect(() => {
    if (shouldHandleSuggestionsSubmit) {
      handleSubmit(
        value,
        sanitizedValue,
        selectedSuggestion,
        addHistoryFromSubmit,
        addHistoryItem,
        suggestions,
        searchHref,
      );
      setIsSubmitted(false);
      collapse();
    }
  }, [shouldHandleSuggestionsSubmit]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (selectedAkinatorCompletion) {
      sendAkinatorCompletionAnalyticsEvent(index, selectedAkinatorCompletion);
      updateValue(getUpdatedQueryByCompletionType(value, selectedAkinatorCompletion));
      return;
    }

    setIsSubmitted(true);
    trackingHelper.dispatchSearchEvent(value);
    if (isLmcode && !productRedirectionUrlLoading && !productRedirectionUrl) {
      dispatch(ProductRedirectionEvents.request, {
        lmcode: sanitizedValue,
        regionId: currentRegionId,
      });
      return;
    }
    if (suggestionsMismatch && !selectedSuggestion) {
      return;
    }
    handleSubmit(
      value,
      sanitizedValue,
      selectedSuggestion,
      addHistoryFromSubmit,
      addHistoryItem,
      suggestions,
      searchHref,
    );
    collapse();
  };

  const updateValue = useCallback(
    (nextValue: string) => {
      setValue(nextValue);
      reset();
      if (nextValue === '') {
        resetServices();
        resetSponsoredProducts();
        resetSponsoredBrands();
      }
      dGetSuggestions(normalizeInput(nextValue));
    },
    [dGetSuggestions, reset, resetServices, resetSponsoredProducts, resetSponsoredBrands],
  );

  useUpdateEffect(() => {
    if (transitionState === 'collapsed') {
      setValue(initialValue);
      reset();
      if (initialValue === '') resetServices();
    } else if (transitionState === 'needsExpand') {
      updateValue(value);
    }
  }, [initialValue, updateValue, transitionState, reset]);

  return {
    isSrp,
    onSubmit,
    value,
    updateValue,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    index,
    selectedSuggestion,
    slicedSuggestions,
    akinatorCompletions,
    showAkinatorCompletions,
  };
};
